'use client';

import { useCallback, useMemo, useState } from 'react';

import type { PopperProps } from '@mui/material/Popper';
import type { VirtualElement } from '@popperjs/core';
import type { Dispatch, MouseEvent, MouseEventHandler, SetStateAction } from 'react';

export type AnchorEl = null | VirtualElement | Element | (() => VirtualElement);

// type MouseAndTouchHandler =(event: MouseEvent | TouchEvent) => void;
type MouseAndTouchHandler = (event: MouseEvent) => void;

export type UsePopper = (
  { id, isOpen, anchorEl }: { id: string; isOpen?: boolean; anchorEl?: PopperProps['anchorEl'] }
) => {
  isOpen: boolean;
  anchorEl: PopperProps['anchorEl'];
  setAnchorEl: Dispatch<
    SetStateAction<
      | VirtualElement
      | HTMLElement
      | (() => HTMLElement)
      | (() => VirtualElement)
      | null
      | undefined
    >
  >;
  handleOpen: MouseAndTouchHandler;
  handleClose: MouseAndTouchHandler;
  handleToggle: MouseAndTouchHandler;
  id: PopperProps['id'];
};

export const usePopper: UsePopper = (
  { id: a11yId = '', isOpen: initialIsOpen = false, anchorEl: initialAnchorEl = null }
) => {
  const [isOpen, setIsOpen] = useState( initialIsOpen );
  const [anchorEl, setAnchorEl] = useState<PopperProps['anchorEl']>( initialAnchorEl );
  const id = useMemo(
    (): string | undefined => (isOpen && Boolean( anchorEl ) ? a11yId : void 0),
    [a11yId, isOpen, anchorEl]
  );

  const handleOpen: MouseAndTouchHandler = useCallback( (event): void => {
    setAnchorEl( event.target as AnchorEl );
    setIsOpen( true );
  }, [] );

  const handleClose: MouseAndTouchHandler = useCallback( (): void => {
    setAnchorEl( null );
    setIsOpen( false );
  }, [] );

  const handleToggle: MouseAndTouchHandler = useCallback( (event): void => {
    setAnchorEl( event.currentTarget );
    setIsOpen((previousOpen) => !previousOpen);
  }, [] );

  return {
    isOpen,
    anchorEl,
    setAnchorEl,
    handleOpen,
    handleClose,
    handleToggle,
    id,
  };
};
